import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import { UnknownAppCrash } from '../UnknownAppCrash/UnknownAppCrash';

interface Props {
  children: ReactNode;

  /**
   * Custom fallback UI to display when an error occurs. If not provided,
   * a default error message UI will be shown.
   */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * A reusable error boundary component designed to catch unhandled errors
 * that results in a total application crash. Handles sending error logs to
 * analytics services such as DataDog.
 *
 * This component contains a default UI to show to the user when there is a crash.
 * To replace this default UI, pass a `fallback` prop.
 *
 * @example
 * // Basic usage
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With custom fallback UI
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logUnknownErrorResponse(error, {
      key: 'error.boundary.unhandled.crash',
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      } else {
        return <UnknownAppCrash />;
      }
    }

    return this.props.children;
  }
}
